import { useEffect, useState } from "react";
import {
  CreditCard,
  CheckCircle2,
  Clock,
  Download,
  Copy,
  Receipt,
  XCircle,
  Smartphone,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatMoney(cents, currency = "eur") {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: (currency || "eur").toUpperCase(),
  }).format((cents || 0) / 100);
}

const STATUS_META = {
  paid: { label: "Payé", color: "#22C55E", Icon: CheckCircle2 },
  pending: { label: "En attente", color: "#F59E0B", Icon: Clock },
  failed: { label: "Échec", color: "#EF4444", Icon: XCircle },
  expired: { label: "Expiré", color: "#94A3B8", Icon: XCircle },
  refunded: { label: "Remboursé", color: "#94A3B8", Icon: Receipt },
};

export default function Payments() {
  const [history, setHistory] = useState([]);
  const [wero, setWero] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [h, w] = await Promise.all([
          api.get("/payments/me"),
          api.get("/payments/wero"),
        ]);
        setHistory(h.data);
        setWero(w.data);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const copyNumber = () => {
    if (!wero) return;
    const n = wero.number.replace(/\s/g, "");
    navigator.clipboard?.writeText(n);
    toast.success("Numéro copié");
  };

  if (loading) {
    return <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement…</div>;
  }

  return (
    <div className="space-y-8" data-testid="payments-page">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-emerald-300 mb-2 flex items-center gap-2">
          <Smartphone className="w-3 h-3" /> Paiement Wero
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
          Paiement
        </h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Envoyez votre paiement directement via <b className="text-slate-200">Wero</b> au numéro
          de l'association. Le coach ou le DT validera votre paiement dans l'app.
        </p>
      </div>

      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-[#0B0F17] border border-emerald-500/20 p-6 sm:p-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl" />
        <div className="relative space-y-4">
          <div className="text-[10px] font-mono-stat uppercase tracking-widest text-emerald-300">
            Numéro Wero de l'association
          </div>
          <div className="flex items-end gap-3 flex-wrap">
            <div
              data-testid="wero-number"
              className="font-display font-black text-3xl sm:text-5xl text-white leading-none tracking-wider"
            >
              {wero?.number || "—"}
            </div>
            <button
              data-testid="copy-wero"
              onClick={copyNumber}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase tracking-wide"
            >
              <Copy className="w-3.5 h-3.5" /> Copier
            </button>
          </div>
          <div className="text-sm text-slate-400">
            Bénéficiaire : <b className="text-slate-200">{wero?.beneficiary}</b>
          </div>
          <div className="mt-3 p-3 rounded-lg bg-slate-800/60 border border-white/5 text-xs text-slate-300 leading-relaxed">
            {wero?.instructions}
          </div>
        </div>
      </div>

      <div className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden">
        <div className="p-4 border-b border-white/10">
          <div className="font-display font-bold text-lg uppercase">Mon historique</div>
          <div className="text-[11px] text-slate-500 font-mono-stat mt-1">
            Les paiements validés par le coach apparaissent ici.
          </div>
        </div>
        {history.length === 0 ? (
          <div className="p-8 text-center text-slate-500 text-sm">
            Aucun paiement pour le moment
          </div>
        ) : (
          <div className="divide-y divide-white/5">
            {history.map((t) => {
              const meta = STATUS_META[t.payment_status] || STATUS_META.pending;
              const Icon = meta.Icon;
              return (
                <div
                  key={t.session_id}
                  data-testid={`payment-tx-${t.session_id}`}
                  className="p-4 flex items-center gap-3 flex-wrap"
                >
                  <div
                    className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: `${meta.color}22`, color: meta.color }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm truncate">
                      {t.label || "Paiement"}
                    </div>
                    <div className="text-[11px] text-slate-500 font-mono-stat">
                      {formatMoney(t.amount, t.currency || "eur")} · {formatDate(t.created_at)}
                      {t.recorded_by_name && (
                        <> · Validé par {t.recorded_by_name}</>
                      )}
                    </div>
                  </div>
                  <span
                    className="text-[10px] font-mono-stat uppercase tracking-widest px-2 py-1 rounded-full whitespace-nowrap"
                    style={{
                      background: `${meta.color}22`,
                      color: meta.color,
                      border: `1px solid ${meta.color}55`,
                    }}
                  >
                    {meta.label}
                  </span>
                  {t.payment_status === "paid" && (
                    <a
                      data-testid={`download-receipt-${t.session_id}`}
                      href={`${process.env.REACT_APP_BACKEND_URL}/api/payments/receipt/${t.session_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-[11px] font-bold uppercase tracking-wide"
                    >
                      <Download className="w-3.5 h-3.5" /> Reçu
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
